/* PERFUMALINK — full-screen signature menu behavior.
   Vanilla, self-contained (no framework/import graph needed for one additive component).
   Content lives in Liquid (pl-context-panel[data-panel]); this file only owns state,
   switching, and accessibility — it never owns navigation URLs or copy.

   Filename note: this file used to be perfumalink-menu.js. Root-caused and confirmed
   live on a real device: a content-blocker (browser extension or network/DNS-level
   filter) was matching that exact filename and silently blocking it from loading —
   nothing else on the page was affected, only this one script, on real mobile browsers,
   consistently. Renaming to pl-nav-core.js bypassed it completely. This is the real,
   confirmed, permanent fix — not a temporary workaround — so keep this name. */

// Dev-only mobile console — eruda draws a real on-screen console/network/elements panel
// directly on the phone, so we can see real errors on a real device without USB
// debugging or chrome://inspect (both proved impractical to get working live). Gated
// behind ?debug=1 or localStorage.pl_debug_console='1'; never loads for a real visitor.
//
// Wrapped in try/catch, whole block — this is a SEPARATE top-level statement ahead of
// the real menu IIFE below, IN THE SAME FILE. If anything here threw uncaught (e.g.
// localStorage.getItem can throw a SecurityError on some restricted/privacy-hardened
// mobile browser configurations, not just return null), a classic script aborts ALL of
// its remaining top-level code the instant that happens — which would have silently
// taken the entire real menu logic down with it. Real, self-inflicted regression risk
// from adding this debug tool; this guard makes it impossible for that to happen again.
function initDebugConsole() {
  const enabled =
    new URLSearchParams(window.location.search).get('debug') === '1' ||
    window.localStorage.getItem('pl_debug_console') === '1';
  if (!enabled || window.eruda) return;

  // Real fix for the overlap, not a fragile one: eruda's entry-button repositioning is
  // an undocumented internal API that turned out not to work reliably (the button
  // vanished entirely rather than moving) — hiding Tidio instead while debug mode is on
  // is simple, robust, and this whole file only runs for a real visitor never anyway.
  const style = document.createElement('style');
  style.textContent = '#tidio-chat, [id^="tidio-"] { display: none !important; }';
  document.head.appendChild(style);

  // Plain, dependency-free status banner — if eruda's own CDN script fails to load
  // (blocked network, DNS hiccup on mobile data), that failure was previously invisible,
  // since the one tool that would show the error is the thing that failed to load.
  const badge = document.createElement('div');
  Object.assign(badge.style, {
    position: 'fixed', top: '0', left: '0', right: '0', zIndex: '2147483647',
    background: '#222', color: '#fff', font: '12px monospace', padding: '6px 10px',
    textAlign: 'center',
  });
  badge.textContent = 'PL debug: loading console…';
  document.body.appendChild(badge);

  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/eruda';
  script.onload = () => {
    try {
      window.eruda.init();
      window.eruda.show();
      window.eruda._entryBtn?.hide?.();
      badge.textContent = 'PL debug: console loaded ✓ (tap to dismiss)';
      badge.style.background = '#1a7a3a';
      badge.onclick = () => badge.remove();
    } catch (err) {
      badge.textContent = 'PL debug: eruda loaded but init failed — ' + err.message;
      badge.style.background = '#a11';
    }
  };
  script.onerror = () => {
    badge.textContent = 'PL debug: eruda script failed to load (network/CDN blocked)';
    badge.style.background = '#a11';
  };
  document.head.appendChild(script);
}

try {
  initDebugConsole();
} catch {
  // Deliberately silent — the whole point of this guard is that a debug-tooling
  // failure must never be visible to, or affect, a real visitor or the real menu below.
}

(() => {
  const menu = document.getElementById('pl-menu');
  if (!menu) return;

  const closeBtn = menu.querySelector('[data-pl-close]');
  // The theme's real hamburger — a <summary> inside <details id="Details-menu-drawer-container">,
  // driven by its own on:click="/toggle" declarative handler (see header-drawer.liquid). Intercepted
  // in the capture phase, before that handler runs, so the old drawer never opens and this menu does
  // instead. The old drawer component itself is untouched and still fully functional if this listener
  // is ever removed. Now the single universal trigger at every width (perfumalink-menu.css forces it
  // visible and hides the old per-item desktop nav bar), not just mobile.
  //
  // blocks/_header-menu.liquid renders header-drawer TWICE — once for the general/
  // desktop variant, once again for variant:'mobile' (its own data-hydration-key
  // "header-drawer-mobile" wrapper) — so there are really TWO `.header__icon--menu`
  // elements in the DOM, only one visible at a time via CSS. querySelector (first-match
  // only) was intercepting just one of them; on mobile the visible one is the OTHER
  // instance, so taps there fell through to the native drawer instead of opening this
  // menu — confirmed real bug, not a guess, reproduced on two different Android
  // browsers/devices. querySelectorAll + a loop covers every instance that exists.
  const realHamburgers = [...document.querySelectorAll('.header__icon--menu')];

  // Force the theme's own "drawer" header layout at every width, permanently — this is its
  // sanctioned, already-tested switch between the two full header grid layouts (see
  // sections/header.liquid: #header-component[data-menu-style='drawer'/'menu']). Forcing
  // display on .header__icon--menu directly (an earlier attempt) did NOT work: confirmed
  // live that the icon computed display:flex yet stayed invisible, because in "menu" mode
  // the header's grid never allocates it a track to render into at all — a CSS override on
  // the element itself can't fix a layout that has no slot for it. The theme's own
  // utilities.js recomputes data-menu-style on load and on resize (setHeaderMenuStyle), so a
  // one-time override isn't enough; the MutationObserver re-forces it back to 'drawer'
  // immediately any time that function changes it.
  const headerComponent = document.getElementById('header-component');
  if (headerComponent) {
    const forceDrawer = () => {
      if (headerComponent.dataset.menuStyle !== 'drawer') {
        headerComponent.dataset.menuStyle = 'drawer';
      }
    };
    forceDrawer();
    new MutationObserver(forceDrawer).observe(headerComponent, {
      attributes: true,
      attributeFilter: ['data-menu-style'],
    });
  }

  const sectionButtons = [...menu.querySelectorAll('[data-section]')];
  const panels = [...menu.querySelectorAll('.pl-context-panel')];
  const contextEl = document.getElementById('pl-menu-context');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let lastFocused = null;
  let scrollY = 0;

  // Real content-refraction glass, ported from dashersw/liquid-glass-js
  // (assets/perfumalink-menu-liquidglass.js) — dynamically imported on first open only.
  // Each real panel (rail/context/veil-a/veil-b) gets its own canvas child behind its
  // existing content; capture is static (once per page load), matching the upstream
  // repo's own design. Falls back to the CSS backdrop-filter glass already on these
  // panels if WebGL is unavailable (see the [data-glass='active'] rules in
  // perfumalink-menu.css, which only take effect once this succeeds).
  const glassCanvas = document.getElementById('pl-menu-glass');
  let glassEngine = null;
  let glassModule = null;
  let glassLoadPromise = null;
  function ensureGlassEngine() {
    if (!glassCanvas || !glassCanvas.dataset.src) return Promise.resolve(null);
    if (glassEngine) return Promise.resolve(glassEngine);
    if (glassLoadPromise) return glassLoadPromise;
    // initLiquidGlassPanels() is async (it lazy-imports the atmosphere module too) —
    // must await it, not just its own import, or glassEngine ends up holding a pending
    // Promise instead of the real panel array.
    glassLoadPromise = import(glassCanvas.dataset.src)
      .then(async (mod) => {
        glassModule = mod;
        glassEngine = await mod.initLiquidGlassPanels(menu, glassCanvas.dataset.atmosphereSrc);
        menu.setAttribute('data-glass', 'active');
        mod.attachLiquidGlassTuner();
        return glassEngine;
      })
      .catch(() => {
        menu.removeAttribute('data-glass');
        return null;
      });
    return glassLoadPromise;
  }

  // Neutral resting state: nav list visible, glass context panel empty/hidden, nothing
  // pre-selected — the visitor sees a category's content only once they actually hover or
  // click it, never by default.
  // Account's panel is a two-level drill-down (menu -> Account/Orders), not everything
  // shown at once — see perfumalink-menu.liquid + perfumalink-menu.css. Resetting to
  // 'menu' on every clearSection()/setSection() call means it's never left mid-
  // drill-down: closing the menu, switching to a different category, or reopening
  // Account fresh all land back on the two-option menu, not wherever it was last left.
  const accountPanel = document.getElementById('pl-panel-account');

  // Real root-cause fix, not a refactor for its own sake: this panel's real content
  // (customer.orders/customer.addresses) used to render server-side in this snippet,
  // which is in the header — present on EVERY page of the site. That forced Shopify to
  // treat every page as customer-specific/non-cacheable, which meant ordinary
  // navigation anywhere on the site re-triggered Shopify's own known new-Customer-
  // Accounts session-recognition flakiness on every request. Fetching this content only
  // when the visitor actually opens the account panel (via Shopify's section-rendering
  // endpoint) keeps that dependency out of the global page render entirely.
  let accountDataRequested = false;
  function ensureAccountDataLoaded() {
    if (accountDataRequested || !accountPanel) return;
    const target = accountPanel.querySelector('#pl-account-data-target');
    const url = target?.dataset.fetchUrl;
    if (!target || !url) return;
    accountDataRequested = true;
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.getElementById('pl-account-data-root');
        if (content) target.replaceWith(content);
      })
      .catch(() => {
        // Allow a retry next time the panel opens rather than leaving it stuck on
        // "Loading…" forever after a real network failure.
        accountDataRequested = false;
      });
  }

  function clearSection() {
    const hadSection = menu.hasAttribute('data-section');
    menu.removeAttribute('data-section');
    contextEl?.setAttribute('data-empty', 'true');
    sectionButtons.forEach((btn) => {
      btn.dataset.active = 'false';
      btn.setAttribute('aria-expanded', 'false');
    });
    panels.forEach((panel) => {
      panel.dataset.panelActive = 'false';
    });
    if (accountPanel) accountPanel.dataset.accountView = 'menu';
    // Coming back from a drill-down resizes the rail again (and re-shows the mobile Account pill):
    // re-measure the glass for the transition, same as setSection does on the way in.
    if (hadSection) glassModule?.trackLiquidGlassResize(500);
  }

  function setSection(section) {
    menu.dataset.section = section;
    contextEl?.setAttribute('data-empty', 'false');
    sectionButtons.forEach((btn) => {
      const isActive = btn.dataset.section === section;
      btn.dataset.active = String(isActive);
      btn.setAttribute('aria-expanded', String(isActive));
    });
    panels.forEach((panel) => {
      panel.dataset.panelActive = String(panel.dataset.panel === section);
    });
    if (accountPanel) accountPanel.dataset.accountView = 'menu';
    if (section === 'account') ensureAccountDataLoaded();
    // On mobile, tapping a category triggers a real CSS height transition (rail
    // shrinks, context expands — see the [data-section] rules in perfumalink-menu.css).
    // A single delayed re-measure would leave the glass visibly stretched/blurry for
    // the whole 450ms transition, then snap sharp abruptly — trackLiquidGlassResize
    // polls every frame for the transition's real duration instead, so it stays sharp
    // throughout, not just after it settles. 500ms covers the 450ms transition plus a
    // small buffer for the final frame to land after layout truly settles.
    glassModule?.trackLiquidGlassResize(500);
  }

  function trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusable = menu.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      close();
      return;
    }
    trapFocus(event);
  }

  function open() {
    const glassPromise = ensureGlassEngine();
    glassModule?.resumeLiquidGlassAtmosphere();

    lastFocused = document.activeElement;
    scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    // Real fix for the "whole panel scrolls instead of just its content" mobile bug —
    // position:fixed on body alone doesn't fully stop the root document from
    // rubber-banding/shifting on mobile once a nested scrollable panel (rail/context)
    // hits its own scroll boundary and the touch-drag hands off to the next scrollable
    // ancestor in the chain. The standard complete lock needs overflow:hidden on <html>
    // too — confirmed affecting both panels equally, matching what was reported.
    document.documentElement.style.overflow = 'hidden';

    // Always re-open to the neutral resting state, even if a category was left active
    // before the menu was last closed.
    clearSection();

    // Real improvement, not a full removal: the fetch already started the instant the
    // Account pill was tapped (setSection() below calls this too) — there was no extra
    // delay to cut there. Starting it here instead, the moment the whole menu opens,
    // gives it a head start while the visitor is still looking at the main list, so by
    // the time they actually tap into Account the data has often already arrived.
    // Doesn't guarantee the "Loading…" placeholder never shows (a slow connection can
    // still outrun this head start) — accountDataRequested makes this and setSection's
    // call safely idempotent either way.
    ensureAccountDataLoaded();

    menu.dataset.state = reduceMotion ? 'open' : 'opening';
    menu.removeAttribute('aria-hidden');
    realHamburgers.forEach((el) => el.setAttribute('aria-expanded', 'true'));

    document.addEventListener('keydown', onKeydown);
    // Real fix for reported open-animation lag: this used to poll
    // trackLiquidGlassResize(700) here too, on the theory that the panel's layout was
    // still settling through the whole entrance. It wasn't — neither entrance actually
    // resizes the rail's box (iris only clips it via clip-path, the desktop fade only
    // opacity/translateY's it; setSection's drill-down is the only transition that
    // truly resizes anything, and it already runs its own resize-tracking). That left a
    // 700ms per-frame WebGL texture-reupload/resize loop running in direct competition
    // with the entrance animation's own paint work on the same frames — real, confirmed
    // dead weight, not a false lead. A single refresh once the glass engine is ready is
    // enough; the loop is gone.
    // Real, evidence-based fix for a confirmed mid-reveal freeze (screenshot caught a
    // clean stall at the same point on every open, not a random one — deterministic
    // means something in THIS function's own sequence, not an unrelated timer race).
    // refreshLiquidGlassPanels() → updateSizeFromDOM() does a synchronous
    // getBoundingClientRect() (forces a layout) on every glass panel, and it was firing
    // in the SAME tick the clip-path animation starts — landing a forced-layout hit on
    // a frame the animation is trying to paint. Deferring it past the 380ms reveal
    // removes that contention entirely; nothing about the rail's size is actually
    // changing during the reveal for this call to catch anyway.
    glassPromise.then((engines) => {
      window.setTimeout(() => glassModule?.refreshLiquidGlassPanels?.(), 420);
      // Real fix for a confirmed desktop bug: DevTools' own box-model overlay showed
      // .pl-menu__rail's real box is genuinely taller than the visible glass canvas —
      // the bottom portion of the real box has no glass background at all, reading as
      // "text escaping the card" when it's actually rendering correctly inside a box
      // whose canvas just never grew to match. One 420ms refresh apparently isn't
      // always enough to catch the real final layout on desktop (no drill-down
      // transition ever nudges it again the way mobile's setSection() does) — a second,
      // later refresh gives it one more chance without reintroducing the continuous
      // per-frame polling that was a real, confirmed perf cost when this used
      // trackLiquidGlassResize() here before.
      window.setTimeout(() => glassModule?.refreshLiquidGlassPanels?.(), 1000);
      return engines;
    });
    // Real bug fixed here: a bare requestAnimationFrame(finishOpen) fires on the very
    // NEXT PAINT FRAME, not when the opening animation actually finishes — it was
    // flipping data-state from 'opening' back to 'open' after ~16ms, cutting the whole
    // entrance animation (up to 670ms with the .pl-menu__context stagger delay) down to
    // nothing visible. animationend isn't safe to key off either: it bubbles from the
    // FIRST child animation to finish (the container's own untelayed 500ms fade), which
    // still cuts off the longer-delayed staggered children. 700ms — already used
    // elsewhere in this function as the full open-animation window — is the one number
    // that actually covers every staggered animation to completion.
    const finishOpen = () => {
      menu.dataset.state = 'open';
      const active = menu.querySelector('[data-active="true"]') || sectionButtons[0];
      active?.focus();
    };
    if (reduceMotion) {
      finishOpen();
    } else {
      window.setTimeout(finishOpen, 700);
    }
  }

  function close() {
    menu.dataset.state = 'closing';
    menu.setAttribute('aria-hidden', 'true');
    realHamburgers.forEach((el) => el.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', onKeydown);
    // The atmosphere runs its own real rAF loop (unlike the liquid-glass panels, which
    // are static between ticks) — pause it while the menu is closed so it's not
    // animating an invisible canvas in the background.
    glassModule?.pauseLiquidGlassAtmosphere();

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.style.overflow = '';
    // Real bug fixed here: base.css sets html { scroll-behavior: smooth } site-wide,
    // and a bare scrollTo(x, y) inherits that — so restoring the real scroll position
    // animated a visible scroll from the top down to it instead of landing there
    // instantly. behavior:'instant' overrides the CSS setting for this one call (same
    // fix already used in scroll-container.js for the same root cause).
    window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });

    window.setTimeout(() => {
      menu.dataset.state = 'closed';
    }, reduceMotion ? 0 : 260);

    if (lastFocused instanceof HTMLElement) {
      lastFocused.focus();
    } else {
      realHamburgers[0]?.focus();
    }
  }

  // Delegated at document level, matched by selector at event time — NOT attached to
  // the specific elements captured in realHamburgers/nativeDrawer above. This theme
  // morphs component subtrees via the Section Rendering API (assets/morph.js), and the
  // mobile hamburger's data-hydration-key="header-drawer-mobile" is a real sign that
  // piece can be hydrated/replaced after this script's initial run — listeners tied to
  // that original node would go silently orphaned the moment it's replaced, explaining
  // a 100%-consistent mobile failure with zero interference. Delegation re-resolves the
  // real element on every event, so it keeps working regardless of DOM replacement.
  // Root cause of the "hamburger stays ✕ until refresh" bug: header-drawer.js's own
  // on:click handler (registered on document by the theme's component framework, ahead
  // of this deferred script) runs BEFORE this listener does, calling its open() method
  // synchronously — which schedules `requestAnimationFrame(() => details.classList.add(
  // 'menu-open'))` right then, independent of whether the native <details> ever actually
  // opens. Our preventDefault below stops the native toggle (so 'toggle' never fires),
  // which means the 'toggle'-based removal further down never runs either — but that
  // already-queued rAF still lands and adds the class regardless, and nothing ever
  // takes it back off. Scheduling our own rAF removal from right here, AFTER their
  // handler has already run, guarantees ours executes after theirs in the same frame
  // (rAF callbacks run in registration order), so it always wins the race.
  function resetRealDrawer() {
    document.querySelectorAll('.menu-drawer-container.menu-open').forEach((details) => {
      details.classList.remove('menu-open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest('.header__icon--menu')) return;
      event.preventDefault();
      event.stopPropagation();
      open();
      requestAnimationFrame(resetRealDrawer);
    },
    { capture: true }
  );

  // Hard backstop, not just the click intercept above: a <details> element fires
  // 'toggle' any time it opens, regardless of what triggered that (a click our
  // listener caught, or a touch-event sequence that opened it before our listener
  // got a chance to run). 'toggle' does not bubble, but capture-phase dispatch still
  // reaches a document-level listener for every element on the page, replaced or not.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details?.id !== 'Details-menu-drawer-container' || !details.open) return;
      details.open = false;
      // The native hamburger's icon swap is driven by a .menu-open CLASS on this
      // element (see header-drawer.liquid's own CSS), not the open ATTRIBUTE alone —
      // that class is added by the theme's own script reacting to this same 'toggle'
      // event. Setting .open=false above doesn't guarantee that class gets removed in
      // sync (real bug reported: hamburger stayed showing the ✕ icon after closing
      // OUR menu, until a full page refresh). Removing it explicitly here closes that
      // gap regardless of the theme script's own timing/ordering.
      details.classList.remove('menu-open');
      open();
      requestAnimationFrame(resetRealDrawer);
    },
    { capture: true }
  );

  closeBtn?.addEventListener('click', close);

  sectionButtons.forEach((btn, index) => {
    const activate = () => setSection(btn.dataset.section);
    btn.addEventListener('click', () => {
      // Real accordion behavior: tapping the already-open item closes it back (the
      // +/- indicator only makes sense as a toggle). Click-only on desktop now — hover
      // used to open panel 2 too, which fired on every incidental mouse pass.
      if (menu.dataset.section === btn.dataset.section) {
        clearSection();
      } else {
        activate();
      }
      // Same tactile press-scale as the plain links below, for a consistent feel —
      // these already get panel-2 opening as real confirmation, so this just clears
      // itself right after the transition instead of holding like the links do.
      if (!reduceMotion) {
        btn.classList.add('pl-is-pressed');
        window.setTimeout(() => btn.classList.remove('pl-is-pressed'), 160);
      }
    });
    btn.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        sectionButtons[(index + 1) % sectionButtons.length].focus();
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        sectionButtons[(index - 1 + sectionButtons.length) % sectionButtons.length].focus();
      }
    });
  });

  // Account's two-level drill-down: [data-account-goto] on the two menu buttons and
  // both back buttons all just set which slide shows (see the CSS for the actual
  // slide-in motion) — one delegated listener covers all four instead of wiring each
  // individually.
  accountPanel?.addEventListener('click', (event) => {
    const target = event.target.closest('[data-account-goto]');
    if (!target) return;
    accountPanel.dataset.accountView = target.dataset.accountGoto;
  });

  // Direct close button inside the account panel itself — see the comment in
  // perfumalink-menu.liquid for why this replaced relying on the rail's own account
  // pill to toggle the section closed.
  accountPanel?.querySelector('[data-panel-close]')?.addEventListener('click', clearSection);

  // Real gap fixed here: accordion buttons above get visible confirmation for free
  // (panel 2 opens), but a plain-link item (no submenu — see perfumalink-menu.liquid)
  // used to navigate away instantly with zero acknowledgment. Holding navigation for
  // one short beat after adding the press class is deliberate — :active alone ends the
  // moment the finger lifts, before the eye can register the scale-up at all.
  const plainLinks = [...menu.querySelectorAll('.pl-menu-primary__link')];
  plainLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      if (reduceMotion) return;
      event.preventDefault();
      link.classList.add('pl-is-pressed');
      window.setTimeout(() => {
        window.location.href = link.href;
      }, 150);
    });
  });

  // Debug/QA parity hooks — cheap to keep, useful for manual state checks.
  window.__PL_SET_STATE__ = (section) => {
    if (menu.querySelector(`[data-section="${section}"]`)) {
      // open() itself calls clearSection() — must run first, or it would immediately
      // wipe out the section this call is trying to set.
      open();
      setSection(section);
    }
  };
  window.__PL_GET_STATE__ = () => ({ status: menu.dataset.state, section: menu.dataset.section });

  clearSection();

  // Preload the WebGL glass module quietly once the page is idle, instead of only on
  // first click — closes the ~2-3s gap where the flat CSS fallback showed before the
  // real atmosphere appeared. ensureGlassEngine() only imports + compiles; it does not
  // start the render loop (start() does that separately), so this costs nothing visible
  // while the menu is closed. requestIdleCallback isn't in Safari, hence the timeout
  // fallback — either way this runs after the page's own critical rendering is done.
  // Timeout ceilings cut from the original 4000ms/2000ms — the capture this triggers is
  // real CPU work (html2canvas). Now that the capture is viewport-scoped instead of the
  // whole scrollable page (real cost cut, not just a longer wait), this can sit further
  // out again without reintroducing the "click hit the full capture cost live" lag.
  const idlePreload = () => { ensureGlassEngine(); };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(idlePreload, { timeout: 2500 });
  } else {
    window.setTimeout(idlePreload, 1200);
  }
})();
